<template>
  <div class="merch-page p-4 md:p-8 bg-base-100 min-h-screen relative">
    <!-- Toast Notification -->
    <div class="toast toast-top toast-center">
      <div
        v-if="toast.show"
        :class="`alert ${toast.type === 'success' ? 'alert-success' : 'alert-error'}`"
      >
        <span>{{ toast.message }}</span>
      </div>
    </div>
    <div class="container mx-auto">
      <div
        class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4"
      >
        <h2 class="text-4xl font-bold text-center text-base-content">
          Merchandise
        </h2>
        <button
          v-if="loggedIn"
          class="btn btn-primary"
          @click="openNewItemModal"
        >
          <i class="fas fa-plus mr-2"></i> Add Item
        </button>
      </div>

      <div
        v-if="showInstructions"
        role="alert"
        class="alert alert-info shadow-lg mb-6"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          class="stroke-current shrink-0 w-6 h-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
        <span>
          To purchase, please visit our 'Contact Us' page with your order and
          shipping address.
        </span>
        <div>
          <button
            class="btn btn-sm btn-ghost"
            @click="showInstructions = false"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div
        v-if="visibleMerch.length"
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
      >
        <div
          v-for="item in visibleMerch"
          :key="item.id"
          class="card bg-base-200 shadow-xl"
        >
          <figure
            class="h-64 cursor-pointer bg-gradient-to-br from-primary to-accent"
            @click="viewImages(item.images)"
          >
            <img
              :src="item.images[0]"
              :alt="item.name"
              class="h-full w-full object-contain p-2"
            />
          </figure>
          <div class="card-body">
            <h3 class="card-title">{{ item.name }}</h3>
            <p>{{ item.price }}</p>
            <div class="card-actions justify-end">
              <div v-if="loggedIn" class="flex gap-2">
                <button class="btn btn-ghost btn-sm" @click="editItem(item)">
                  <i class="fas fa-pencil-alt"></i>
                </button>
                <button
                  class="btn btn-ghost btn-sm"
                  @click="confirmDelete(item.id)"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-16">
        <p class="text-xl text-base-content opacity-70">
          No merchandise available right now. Check back soon!
        </p>
      </div>
    </div>

    <MerchModal
      v-if="showItemModal"
      :item="selectedItem"
      :is-edit="isEdit"
      @close="closeModal"
      @save-success="showToast($event, 'success')"
      @save-error="showToast($event, 'error')"
    />
    <ImageCarouselModal
      v-if="showCarouselModal"
      :images="imagesToView"
      @close="showCarouselModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useHead } from '@vueuse/head'
import { merchCollection } from '../firebase'
import { doc, deleteDoc } from 'firebase/firestore'
import MerchModal from '../components/MerchModal.vue'
import ImageCarouselModal from '../components/ImageCarouselModal.vue'

const store = useStore()

useHead({
  title: 'Merch - Sqwerv',
  meta: [
    {
      hid: 'sqwervMerchPage',
      name: 'description',
      content:
        'Sqwerv merch where they sell t-shirts, hoodies, hats, pins, lighters, and more.',
    },
  ],
})

const loggedIn = computed(() => store.getters.loggedIn)
const merch = computed(() => store.getters['merch/merch'])
const visibleMerch = computed(() => merch.value.filter((m) => m.visible))

const showInstructions = ref(true)
const showItemModal = ref(false)
const showCarouselModal = ref(false)
const isEdit = ref(false)
const selectedItem = ref(null)
const imagesToView = ref([])
const toast = ref({
  show: false,
  message: '',
  type: 'success', // 'success' or 'error'
})

const showToast = (message, type = 'success') => {
  toast.value = { show: true, message, type }
  setTimeout(() => {
    toast.value.show = false
  }, 3000)
}

const openNewItemModal = () => {
  isEdit.value = false
  selectedItem.value = { name: '', price: '', images: [''], visible: true }
  showItemModal.value = true
}

const editItem = (item) => {
  isEdit.value = true
  selectedItem.value = item
  showItemModal.value = true
}

const closeModal = () => {
  showItemModal.value = false
  selectedItem.value = null
  isEdit.value = false
}

const viewImages = (images) => {
  imagesToView.value = images
  showCarouselModal.value = true
}

const confirmDelete = (itemId) => {
  if (window.confirm('Are you sure you want to delete this item?')) {
    deleteItem(itemId)
  }
}

const deleteItem = async (itemId) => {
  try {
    const docRef = doc(merchCollection, itemId)
    await deleteDoc(docRef)
    showToast('Successfully Deleted Item', 'success')
  } catch (error) {
    console.error('Failed to Delete Item:', error)
    showToast('Failed to Delete Item', 'error')
  }
}
</script>

<style scoped>
.about .object-contain img {
  object-fit: contain;
}
</style>
